import React, { useState } from "react";
import JsonView from "@uiw/react-json-view";
import {
  Collapse,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  TableBody,
  Table,
  Paper,
  Box,
  Chip,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import TimerIcon from "@mui/icons-material/Timer";
import { useTheme } from "@mui/material";
import { safeJsonParse } from "../utils/utils";
import FileCopyIcon from "@mui/icons-material/FileCopy";
interface Column {
  id: string;
  label: React.ReactNode;
  minWidth?: number;
  maxWidth?: number;
  align?: "center" | "left" | "right" | "inherit" | "justify" | undefined;
  format?: (value: number) => string;
}
const columns: readonly Column[] = [
  {
    id: "response",
    label: "ответ",
    minWidth: 80,
    maxWidth: 100,
    align: "center",
  },
  { id: "req_id", label: "Req. ID" },
  { id: "trans_id", label: "Trans. ID" },
  { id: "url", label: "URL путь" },
  {
    id: "headers request",
    label: "Заголовки запроса",
  },
  {
    id: "body request",
    label: "Тело запроса",
  },
  {
    id: "headers response",
    label: "Заголовки ответа",
  },
  {
    id: "body response",
    label: "Тело ответа",
  },
  {
    id: "timer",
    label: <TimerIcon />,
    align: "center",
  },
];
const getStatusColor = (status: number) => {
  if (status >= 200 && status < 300) return "#4caf50";
  if (status >= 400 && status < 500) return "#ddb823";
  if (status >= 500) return "#ef4444";
  return "#9e9e9e";
};

const LogsTable: React.FC<{ rows: any[]; logType: string }> = ({
  rows,
  logType,
}) => {
  const theme = useTheme();

  const [openRowId, setOpenRowId] = useState<string | null>(null);
  const [collapseType, setCollapseType] = useState<
    "reqHead" | "reqBody" | "resHead" | "resBody" | ""
  >("");

  const isEmptyObject = (value: any) =>
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0;

  const getPreview = (body: string | null) => {
    if (!body) return "—";

    try {
      const parsed = JSON.parse(body);
      if (isEmptyObject(parsed)) return "EMPTY BODY";

      const json = JSON.stringify(parsed);
      return json.length > 20 ? json.slice(0, 20) + "…" : json;
    } catch {
      return "INVALID JSON";
    }
  };

  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString("ru-RU"),
      time: d.toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
    };
  };

  const toggleRow = (id: string, type: typeof collapseType) => {
    setCollapseType(type);
    setOpenRowId(openRowId === id && collapseType === type ? null : id);
  };

  return (
    <Paper
      sx={{
        width: "100%",
        // Заполняет обёртку, а не растёт под содержимое — иначе таблица
        // вытягивалась целиком и обрезалась родителем без прокрутки.
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "none",
        borderRadius: "15px",
      }}
    >
      <TableContainer
        sx={{
          // Прокрутка живёт здесь: залипающая шапка требует, чтобы скроллером
          // был именно TableContainer.
          maxHeight: "100%",
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.primary.main,
            borderRadius: "5px",
          },
        }}
      >
        <Table
          sx={{
            tableLayout: "fixed",
            width: "100%",
          }}
          stickyHeader
          aria-label="sticky table"
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    textTransform: "uppercase",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => {
              const { date, time } = formatDateTime(row.ts);

              return (
                <React.Fragment key={row.request_id}>
                  <TableRow hover>
                    {/* STATUS */}
                    <TableCell>
                      <Box
                        sx={{
                          minWidth: 80,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: 1,
                          backgroundColor: `${getStatusColor(row.status)}20`,
                          color: getStatusColor(row.status),
                          border: `1px solid ${getStatusColor(row.status)}60`,
                          fontFamily: "monospace",
                          fontWeight: 600,
                        }}
                      >
                        <Typography fontSize={14}>{row.status}</Typography>
                      </Box>
                    </TableCell>

                    {/* REQ ID */}
                    <TableCell title={row.req_id}>
                      <Box
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        {row.request_id ?? "-"}
                        <Tooltip title="Copy">
                          <IconButton
                            onClick={() =>
                              navigator.clipboard.writeText(row.request_id)
                            }
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    {/* TRANSACTION ID */}
                    <TableCell title={row.transaction_id}>
                      <Box
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        {row.transaction_id ?? "-"}
                        <Tooltip title="Copy">
                          <IconButton
                            onClick={() =>
                              navigator.clipboard.writeText(row.transaction_id)
                            }
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    {/* URL */}
                    <TableCell>
                      <Box
                        sx={{
                          fontFamily: "monospace",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          cursor: "pointer",
                          "&:hover": {
                            color: "#38bdf8",
                            textDecoration: "underline",
                          },
                        }}
                        onClick={() => navigator.clipboard.writeText(row.url)}
                      >
                        {row.url ?? row.path}
                      </Box>
                    </TableCell>

                    {/* REQ HEADERS */}
                    <TableCell>
                      <Chip
                        label={
                          row.request_headers
                            ? `${Object.keys(row.request_headers).length} headers`
                            : "0 headers"
                        }
                        onClick={() => toggleRow(row.id, "reqHead")}
                        clickable
                        size="medium"
                        sx={{ width: "100%" }}
                      />
                    </TableCell>

                    {/* REQ BODY */}
                    <TableCell>
                      <Chip
                        label={getPreview(row.request_body)}
                        onClick={() => toggleRow(row.id, "reqBody")}
                        clickable
                        size="medium"
                        color="info"
                      />
                    </TableCell>

                    {/* RES HEADERS */}
                    <TableCell>
                      <Chip
                        label={
                          row.response_headers
                            ? `${Object.keys(row.response_headers).length} headers`
                            : "0 headers"
                        }
                        onClick={() => toggleRow(row.id, "resHead")}
                        clickable
                        size="medium"
                        sx={{ width: "100%" }}
                      />
                    </TableCell>

                    {/* RES BODY */}
                    <TableCell>
                      <Chip
                        label={getPreview(row.response_body)}
                        onClick={() => toggleRow(row.id, "resBody")}
                        clickable
                        size="medium"
                        color="info"
                      />
                    </TableCell>

                    {/* TIME */}
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          fontFamily: "monospace",
                          gap: "10px",
                        }}
                      >
                        <Typography fontSize={11}>
                          {date} {time}
                        </Typography>

                        <Box
                          sx={{
                            px: 1,
                            py: 0.3,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor:
                              row.duration_ms < 500
                                ? "success.main"
                                : "error.main",
                            color:
                              row.duration_ms < 500
                                ? "success.main"
                                : "error.main",
                          }}
                        >
                          {row.duration_ms} ms
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* COLLAPSE */}
                  <TableRow>
                    <TableCell colSpan={8} sx={{ p: 0, border: "none" }}>
                      <Collapse
                        in={openRowId === row.id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box m={1} p={1}>
                          <JsonView
                            value={
                              collapseType === "reqHead"
                                ? row.request_headers || {}
                                : collapseType === "reqBody"
                                  ? safeJsonParse(row.request_body)
                                  : collapseType === "resHead"
                                    ? row.response_headers || {}
                                    : collapseType === "resBody"
                                      ? safeJsonParse(row.response_body)
                                      : {}
                            }
                            collapsed={2}
                            displayDataTypes={false}
                            displayObjectSize={false}
                            style={
                              {
                                fontSize: 15,
                                "--w-rjv-key-string":
                                  theme.palette.mode === "dark"
                                    ? "#38bdf8"
                                    : "#002b36",
                              } as React.CSSProperties
                            }
                          />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default LogsTable;
